import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Box, Button, Typography} from '@mui/material';
import {ISelectItem} from '../../../../shared/types';
import './PrivGuard.scss';
import {useTranslation} from 'react-i18next';
import {
  useGetProductionGuardMatchResults,
  useGetProductionGuardView,
  useGetTemplateGuardDetails,
  useSaveProductionGuardView,
} from '../../../../hooks/productions/useProductions';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import TagsTreePanel from '../common/tags-tree-panel/TagsTreePanel';
import {
  PrivGuardFormData,
  privGuardSchema,
} from '../../../../schemas/productions/productionSchema';
import {useNavigate} from 'react-router-dom';
import {
  ModuleType,
  FormAction,
  getInsertFieldTypes,
  createDocumentListRequestPayload,
  createDocumentViewerListRequestPayload,
  ToastType,
  FormMode,
  ProductionStatus,
  STEP_KEY_PRIV_GUARD,
  getStepKeyFromNumber,
} from '../CreateNewProductionUtils';
import RuleCard from './rule-card/RuleCard';
import {
  GetProductionGuardViewRequestParam,
  GetTemplateDetailsRequestParam,
  TagItem,
} from '../../../../api/types/production.type';
import {
  DocumentViewerListIdRequest,
  DocumentListRequestPayload,
} from '../../../../api/types/document.type';
import {zodResolver} from '@hookform/resolvers/zod';
import {useForm} from 'react-hook-form';
import {PlaceholderForPrivDocModal} from './placeholder-for-privdoc-modal/PlaceholderForPrivDocModal';
import {DocumentSelectionWarning} from './document-selection-warning/DocumentSelectionWarning';
import {NoRulesAddedWarning} from './no-rules-added-warning/NoRulesAddedWarning';
import InsertFieldPanel from '../common/insert-field-panel/InsertFieldPanel';
import '../common/insert-field-panel/InsertFieldPanel.scss';
import {
  useDocumentList,
  useDocumentViewerListId,
} from '../../../../hooks/document/useDocument';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../../../redux/slice/docViewSlice';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../redux/slice/docListSlice';
import {SourceTypeCode} from '../../../../shared/constants';
import {clearDocViewState} from '../../../document-viewer/docViewerUtils';
import ArrButton from '../../../common/arr-button/ArrButton';
import {findNodesByIds, hasEnabledChild} from '../CreateNewProductionHelpers';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {setIsFieldsChanged} from '../../../../redux/slice/productionSlice';
import {setCategorizationState} from '../../../../redux/slice/categorizationSlice';

const transformTagTreeData = (
  flatArray: any[],
  enabledForType?: string // Add optional enabledForType parameter
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  const itemMap = new Map();
  const rootItems: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

  const hasEnabledForType = enabledForType && enabledForType !== '';

  // First pass: create all items
  for (const item of flatArray) {
    // Only disable leaf nodes (non-groups) that don't match the enabled type
    // Parent groups will be handled in third pass after hierarchy is built
    const isDisabled =
      hasEnabledForType && item.type !== enabledForType && !item.isGroup; // Don't disable parent groups yet
    itemMap.set(item.id, {
      ...item, // Spread first to get all original properties
      id: item.id,
      label: item.text,
      children: [], // Will be populated in second pass
      isGroup: item.isGroup,
      type: item.type,
      documentCount: item.documentCount,
      isSelected: item.isSelected,
      disabled: isDisabled, // Set disabled property based on type (override any from spread)
    });
  }

  // Second pass: build hierarchy
  for (const item of flatArray) {
    const treeItem = itemMap.get(item.id);

    if (
      item.parent === '#' ||
      item.parent === null ||
      item.parent === undefined
    ) {
      // Root level items (parent is "#" or null/undefined)
      rootItems.push(treeItem);
    } else {
      // Child item - find parent and add to its children
      const parent = itemMap.get(item.parent);
      if (parent) {
        parent.children.push(treeItem);
      } else {
        // Parent not found, treat as root item
        rootItems.push(treeItem);
      }
    }
  }

  // Third pass: disable parent groups if they have no enabled children
  // Using hasEnabledChild utility function

  // Traverse all nodes and disable parent groups with no enabled children
  const processNode = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    if (node.isGroup && hasEnabledForType) {
      // Only disable if it has no enabled children
      if (!hasEnabledChild(node)) {
        node.disabled = true;
      }
    }
    // Process children recursively
    if (node.children) {
      for (const child of node.children) {
        processNode(child);
      }
    }
  };

  for (const rootItem of rootItems) {
    processNode(rootItem);
  }

  return rootItems;
};

// Type for child component ref
export interface StepChildRef {
  save: () => void;
  next: () => void;
}

interface PrivGuardComponentsProps {
  templateId?: number;
  productionId: number;
  productionName: string;
  isProductionSet: boolean;
  getStatus?: (showToast: boolean, status: ToastType, message?: string) => void;
  goNext?: () => void;
  goBack?: () => void;
  onStateChange?: (state: {saveDisabled: any; nextDisabled: boolean}) => void;
  mode?: FormMode;
  isTemplate?: boolean;
  productionStatusCode?: string;
  isUpdateNeeded?: boolean;
  onUpdateNeededChange?: (value: boolean) => void;
  apiCurrentStep?: number;
}

type SelectionItem = {
  type: 'tags' | 'reduction' | 'operator';
  label: string;
  objects?: ExtendedTreeItemProps[];
};

const PrivGuard = forwardRef<StepChildRef, PrivGuardComponentsProps>(
  (
    {
      productionId,
      productionName,
      isProductionSet,
      getStatus,
      goNext,
      goBack: _goBack,
      onStateChange,
      mode = FormMode.CREATE,
      isTemplate = false,
      templateId,
      productionStatusCode,
      isUpdateNeeded = false,
      onUpdateNeededChange,
      apiCurrentStep,
    },
    ref
  ) => {
    const {t} = useTranslation();

    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode == ProductionStatus.INPROGRESS;
    const modeVal = isInProgressOrCompleted ? FormMode.VIEW : mode;
    const isViewMode = modeVal === FormMode.VIEW;

    const [rules, setRules] = useState<string[]>([]);
    const [totalDocumentsCount, setTotalDocumentsCount] = useState<number>(0);
    const [totalFamilyDocumentsCount, setTotalFamilyDocumentsCount] =
      useState<number>(0);
    const [queriesRule, setQueriesRule] = useState<
      {
        SequenceNumber: number | string;
        CriteriaForUI: string;
        CriteriaForDB: string;
        OperatorCode: string;
      }[]
    >([]);
    const isFirstRender = useRef(true);
    const [isDocListClickLoading, setIsDocListClickLoading] = useState(false);
    const [isDocViewClickLoading, setIsDocViewClickLoading] = useState(false);

    const buildCriteriaString = (
      items: SelectionItem[]
    ): {CriteriaForUI: string; CriteriaForDB: string} => {
      // Build fragments for tags/reduction and include operator labels inline with spaces.
      const fragmentsUI: string[] = [];
      const fragmentsDB: string[] = [];

      const formatNames = (objs?: ExtendedTreeItemProps[]) =>
        (objs || []).map((o) => `&quot;${o.label}&quot;`).join(',');

      items.forEach((item) => {
        if (item.type === 'tags') {
          const names = formatNames(item.objects);
          const frag = `tags: [ name:[${names}] ]`;
          fragmentsUI.push(frag);
          fragmentsDB.push(frag);
        } else if (item.type === 'reduction') {
          const names = formatNames(item.objects);
          const frag = `redactions: [ name:[${names}] ]`;
          fragmentsUI.push(frag);
          fragmentsDB.push(frag);
        } else if (item.type === 'operator') {
          // keep operator labels in UI wrapped in single quotes and include plain operator in DB
          fragmentsUI.push(`'${item.label}'`);
          fragmentsDB.push(item.label);
        }
      });

      // Join UI fragments with a single space so operators appear like: "... ] AND redactions: [ ... ]"
      const CriteriaForUI = fragmentsUI.join(' ');
      // DB should contain only the tag/redaction fragments (no operator words)
      const CriteriaForDB = fragmentsDB.join(' ');

      // Normalize small issues (collapse accidental duplicate tokens)
      const normalize = (s: string) =>
        s
          .replace(/name:\s*\[\s*name:\s*\[/g, 'name:[') // collapse duplicated name:[name:[
          .replace(/[ \t\r\n]{1,20}\]/g, ' ]')
          .replace(/\[[ \t\r\n]+\[/g, '[[')
          .replace(/\]{2,}/g, ']')
          .trim();

      return {
        CriteriaForUI: normalize(CriteriaForUI),
        CriteriaForDB: normalize(CriteriaForDB),
      };
    };

    const parseCriteriaString = (criteriaForUI: string): SelectionItem[] => {
      const items: SelectionItem[] = [];

      // Regex to match tags or redactions blocks
      const blockRegex = /(tags|redactions):\s*\[\s*name:\s*\[([^\]]+)\]\s*\]/g;

      // Split string by operators (AND, OR, NOT) but keep operators in the array
      const tokens = criteriaForUI
        .split(/(\bAND\b|\bOR\b|\bNOT\b)/g)
        .filter(Boolean);

      for (let token of tokens) {
        token = token.trim();
        if (!token) continue;

        // Operator case
        if (['AND', 'OR', 'NOT'].includes(token)) {
          items.push({type: 'operator', label: token});
          continue;
        }

        // Now handle tags/redactions in the current token
        let match: RegExpExecArray | null;
        blockRegex.lastIndex = 0; // reset regex for new string
        while ((match = blockRegex.exec(token)) !== null) {
          const type = match[1] === 'tags' ? 'tags' : 'reduction';
          const tagList = match[2]
            .replace(/&quot;/g, '')
            .replace(/\+/g, '')
            .split(',')
            .map((t) => t.trim().replace(/"/g, ''))
            .filter(Boolean);

          if (tagList.length > 0) {
            items.push({
              type,
              label: tagList.join(', '),
              objects: tagList.map((l) => ({
                id: l,
                label: l,
              })) as ExtendedTreeItemProps[],
            });
          }
        }
      }

      return items;
    };

    const handleAddRule = () => {
      if (isViewMode) return;
      setComponentIsDirty(true);
      const newRuleNumber = rules.length + 1;
      const newRuleTitle = `Privileged Rule ${newRuleNumber}`;
      setRules([...rules, newRuleTitle]);
      // Ensure queriesRule has a corresponding empty entry and update OperatorCode for all rules
      setQueriesRule((prev) => {
        const updated = [
          ...prev,
          {
            SequenceNumber: `${prev.length + 1}`,
            CriteriaForUI: '',
            CriteriaForDB: '',
            OperatorCode: '',
          },
        ];
        return updated.map((q, i) => ({
          ...q,
          OperatorCode: i === 0 ? '' : 'OR',
        }));
      });
    };

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {productionSetId, securityGroupId} = useSelector(
      (state: RootState) => state.production
    );

    // Determine moduleType from isProductionSet using enum
    const moduleType = useMemo(
      () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
      [isProductionSet]
    );

    // Don't fire the guard-view API until we have a valid productionId (non-zero)
    const {data: guardViewDataFromProduction} = useGetProductionGuardView(
      {
        SecurityGroupId: securityGroupId,
        productionId,
      } as GetProductionGuardViewRequestParam,
      // enable only when we have a real productionId and not template
      Boolean(productionId && productionId > 0 && !isTemplate)
    );

    const {data: guardViewDataFromTemplate} = useGetTemplateGuardDetails(
      {
        templateId: templateId!,
        securityGroupId: securityGroupId,
      } as GetTemplateDetailsRequestParam,
      // enable only when we have a real templateId and is template
      Boolean(templateId && isTemplate)
    );

    // Use the appropriate data based on isTemplate flag
    const guardViewData = isTemplate
      ? guardViewDataFromTemplate
      : guardViewDataFromProduction;

    const {mutate: saveProductionGuardView} = useSaveProductionGuardView();
    const {mutate: getProductionGuardMatchResults} =
      useGetProductionGuardMatchResults();
    const {mutate: fetchDocumentViewerListId, isPending: isDocViewLoading} =
      useDocumentViewerListId();
    const {mutate: fetchDocumentList, isPending: isDocListLoading} =
      useDocumentList();
    const documentViewerListPayload: DocumentViewerListIdRequest = useMemo(
      () =>
        createDocumentViewerListRequestPayload({
          sourceId: productionId,
          sourceTypeCode: SourceTypeCode.PRODUCTION,
          docViewSourceTypeCode: SourceTypeCode.PRODUCTION,
          sourceDetailOverrides: {
            pureHits: false,
            emailThread: false,
            nearDup: false,
            family: false,
            conceptualSimilar: false,
            uniquePureHits: false,
            uniqueFamilyMembers: false,
            p: false,
            e: false,
            n: false,
            f: false,
            c: false,
            up: false,
            uf: false,
          },
          docsToExcludeOverrides: {
            pureHits: true,
            emailThread: true,
            nearDup: true,
            family: true,
            conceptualSimilar: true,
            uniquePureHits: true,
            uniqueFamilyMembers: true,
            p: true,
            e: true,
            n: true,
            f: true,
            c: true,
            up: true,
            uf: true,
          },
          sourceInfoOverrides: {
            assignmentSource: 'string',
            isMyAssignment: true,
            isInverse: true,
            isDefaultLoad: true,
            isAllChildrenSelected: true,
            destinationContextTypeCode: '',
            isDocumentCount: false,
          },
          docViewSessionOverrides: {
            assignmentSortDetails: [
              {
                sourceID: 0,
                columnManual: '',
                columnOptimized: '',
                columnIDsOptimized: '',
                columnOrderOptimized: '',
                columnIDsManual: '',
                columnOrderManual: '',
                showCompletedOptimized: true,
                showCompletedManual: true,
                columnDisplayManual: '',
                columnDataTypeOptimized: [],
                columnDataTypeManual: [],
              },
            ],
            docsForCodeSame: [],
          },
          sortConfigurationOverrides: {
            manualCols: 'string',
            showCompletedDocOptimized: true,
            showCompletedDocManual: true,
            configuredShowCompletedDoc: true,
          },
          sortConfigurationDocViewOverrides: {
            manualCols: 'string',
            showCompletedDocOptimized: true,
            showCompletedDocManual: true,
            configuredShowCompletedDoc: true,
          },
          sortConfigDocViewOverrides: {
            manualCols: '',
            showCompletedDocOptimized: true,
            showCompletedDocManual: true,
            configuredShowCompletedDoc: true,
          },
        }),
      [productionId]
    );

    const documentListPayload: DocumentListRequestPayload = useMemo(
      () =>
        createDocumentListRequestPayload({
          sourceId: productionId,
          sourceTypeCode: SourceTypeCode.PRODUCTION,
          docViewSourceTypeCode: SourceTypeCode.PRODUCTION,
          sortConfigurationOverrides: {
            manualCols: 'string',
            showCompletedDocOptimized: true,
            showCompletedDocManual: true,
            configuredShowCompletedDoc: true,
          },
          sourceDetailOverrides: {
            pureHits: true,
            emailThread: true,
            nearDup: true,
            family: true,
            conceptualSimilar: true,
            uniquePureHits: true,
            uniqueFamilyMembers: true,
            p: true,
            e: true,
            n: true,
            f: true,
            c: true,
            up: true,
            uf: true,
          },
          docsToExcludeOverrides: {
            pureHits: true,
            emailThread: true,
            nearDup: true,
            family: true,
            conceptualSimilar: true,
            uniquePureHits: true,
            uniqueFamilyMembers: true,
            p: true,
            e: true,
            n: true,
            f: true,
            c: true,
            up: true,
            uf: true,
          },
          sourceInfoOverrides: {
            destinationContextTypeCode: 'DOCLIST',
            isDocumentCount: false,
          },
        }),
      [productionId]
    );

    const handleViewInDocView = () => {
      setIsDocViewClickLoading(true);
      fetchDocumentViewerListId(documentViewerListPayload, {
        onSuccess: (response) => {
          clearDocViewState();
          const masterId =
            response.documentListID ?? response.defaultMasterID ?? 0;
          setTimeout(() => {
            setIsDocViewClickLoading(false);
            dispatch(storeDocumentListMasterId(String(masterId)));
            dispatch(storeSourceTypeCode(SourceTypeCode.PRODUCTION));
            navigate('/DocumentViewer/DocView');
          }, 3000);
        },
        onError: (error) => {
          getStatus?.(
            true,
            ToastType.Error,
            error?.message ??
              t(
                'productionsAndExports.createNewProduction.privGuard.docViewError'
              )
          );
        },
      });
    };

    const handleViewInDocList = () => {
      setIsDocListClickLoading(true);
      fetchDocumentList(documentListPayload, {
        onSuccess: (response) => {
          const masterIdRaw = response.docId ?? 0;
          const masterId = Number(masterIdRaw) || 0;

          if (!masterId) {
            getStatus?.(
              true,
              ToastType.Error,
              t(
                'productionsAndExports.createNewProduction.privGuard.docViewError'
              )
            );
            return;
          }

          clearDocViewState();
          setTimeout(() => {
            setIsDocListClickLoading(false);
            dispatch(
              setDocListBackNavigation({
                label: t('docList.backSource.production'),
                path: `${window.location.pathname}${window.location.search}`,
              })
            );

            dispatch(setDocListId(Number(masterId)));
            navigate('/Document/DocList');
          }, 3000);
        },
        onError: (error) => {
          getStatus?.(
            true,
            ToastType.Error,
            error?.message ??
              t(
                'productionsAndExports.createNewProduction.privGuard.docViewError'
              )
          );
        },
      });
    };

    const handleCategorizationClick = () => {
      // Get current path for backSourcePath
      const currentPath = `${window.location.pathname}${window.location.search}`;

      // Determine isProduction based on isProductionSet
      // isProductionSet === true means PRODUCTION, false means EXPORT
      const isProduction = isProductionSet;

      // Build categorization state using Redux values
      const categorizationState = {
        backSourceLabel: 'Production',
        trainingSetName: 'Production Guard Source',
        backSourcePath: currentPath,
        trainingSetType: 'ExternalSource',
        proviewProductionDetails: {
          productionId: String(productionId),
          productionName: productionName, // From prop
          productionSetId: String(productionSetId), // From Redux
          securityGroupId: String(securityGroupId), // From Redux
          isProduction: isProduction, // Based on isProductionSet
          isProductionExternalSource: true, // Always true as requested
        },
        corpusDetails: {
          id: String(productionId),
          type: 'production',
          text: productionName, // From prop
        },
      };

      // Dispatch the state
      dispatch(setCategorizationState(categorizationState));

      // Navigate to proview with setTimeout(2000) to ensure Redux state is set first
      setTimeout(() => {
        navigate('/Proview/Proview');
      }, 100);
    };
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [componentIsDirty, setComponentIsDirty] = useState<boolean>(false);

    const {
      register: _register,
      handleSubmit,
      setValue,
      formState: {isSubmitting, isDirty, isValid},
      reset,
    } = useForm<PrivGuardFormData>({
      mode: 'onChange',
      resolver: zodResolver(privGuardSchema()),
      defaultValues: {
        action: FormAction.SAVE,
      },
    });
    const isSaveDisabled = isSubmitting || (isSaved && !isDirty);
    const isNextDisabled = isSubmitting || !isValid;

    useEffect(() => {
      if (componentIsDirty) {
        dispatch(setIsFieldsChanged({isFieldsChanged: true}));
      }
    }, [componentIsDirty]);

    const handleDeleteRule = (index: number) => {
      if (isViewMode) return;
      setComponentIsDirty(true);
      const updatedRules = rules.filter((_, i) => i !== index);
      setRules(updatedRules);
      setTotalDocumentsCount(0);
      setTotalFamilyDocumentsCount(0);
      // Also remove the corresponding queriesRule entry and resequence remaining rules
      setQueriesRule((prev) => {
        const filtered = prev
          .filter((_, i) => i !== index)
          .map((q, i) => ({
            ...q,
            SequenceNumber: `${i + 1}`,
          }));
        return filtered.map((q, i) => ({
          ...q,
          OperatorCode: i === 0 ? '' : 'OR',
        }));
      });
    };

    useEffect(() => {
      onStateChange?.({
        saveDisabled: isSaveDisabled,
        nextDisabled: isNextDisabled,
      });
    }, [isSaveDisabled, isNextDisabled, onStateChange]);

    const onSubmit = (data: PrivGuardFormData) => {
      if (data.action == FormAction.SAVE) {
        saveProductionGuardView(
          {
            TotalDocumentsCount: totalDocumentsCount,
            FamilyDocumentsCount: totalFamilyDocumentsCount,
            ProductionId: productionId,
            IsMarkComplete: false,
            PHPrivilegeTagList: '',
            SecurityGroupId: securityGroupId,
            lstProductionGuardQueries: queriesRule,
            PrivDocPlaceHolder: '',
            EnablePrivDocPlaceHolder: false,
            ModuleType: moduleType,
          },
          {
            onSuccess: (savedData) => {
              setIsSaved(true);
              // Notify parent to show success toast
              getStatus?.(true, ToastType.Success, savedData?.message);
              // Update parent state to track that update is needed after save
              onUpdateNeededChange?.(true);
              reset(data);
              setComponentIsDirty(false);
              dispatch(setIsFieldsChanged({isFieldsChanged: false}));
            },
            onError: (err) => {
              // Notify parent to show error toast
              getStatus?.(true, ToastType.Error, err?.message);
            },
          }
        );
      }

      if (data.action == FormAction.NEXT) {
        if (
          !isUpdateNeeded &&
          apiCurrentStep !== undefined &&
          !(
            getStepKeyFromNumber(apiCurrentStep) === STEP_KEY_PRIV_GUARD &&
            productionStatusCode === ''
          )
        ) {
          goNext?.();
          return;
        }
        saveProductionGuardView(
          {
            TotalDocumentsCount: totalDocumentsCount,
            FamilyDocumentsCount: totalFamilyDocumentsCount, // This value might need to be sourced if available
            ProductionId: productionId,
            IsMarkComplete: true, // This is a save from a modal, not the final step
            PHPrivilegeTagList: placeholderPrivTags
              .map((tag) => tag.tagId)
              .join(','),
            SecurityGroupId: securityGroupId,
            lstProductionGuardQueries: queriesRule,
            PrivDocPlaceHolder: placeholderText as string,
            EnablePrivDocPlaceHolder: isPrivPlaceholderEnabled,
            ModuleType: moduleType,
          },
          {
            onSuccess: () => {
              setIsSaved(true);
              // Notify parent to show success toast
              getStatus?.(
                true,
                ToastType.Success,
                t(
                  'productionsAndExports.createNewProduction.common.nextSuccessToast'
                )
              );
              reset(data);
              goNext?.();
            },
            onError: (err) => {
              // Notify parent to show error toast
              getStatus?.(true, ToastType.Error, err?.message);
            },
          }
        );
        dispatch(setIsFieldsChanged({isFieldsChanged: false}));
      }
    };

    const handleQueryUpdate = (ruleIndex: number, items: SelectionItem[]) => {
      if (isViewMode) return;
      setComponentIsDirty(true);
      setTotalDocumentsCount(0);
      setTotalFamilyDocumentsCount(0);

      const {CriteriaForUI, CriteriaForDB} = buildCriteriaString(items);
      setQueriesRule((prev) => {
        const updated = [...prev];
        // Ensure array has slot for this index
        while (updated.length <= ruleIndex) {
          updated.push({
            SequenceNumber: `${updated.length + 1}`,
            CriteriaForUI: '',
            CriteriaForDB: '',
            OperatorCode: '',
          });
        }

        updated[ruleIndex] = {
          SequenceNumber: `${ruleIndex + 1}`,
          CriteriaForUI,
          CriteriaForDB,
          OperatorCode: '', // will be normalized below
        };

        return updated.map((q, i) => ({
          ...q,
          OperatorCode: i === 0 ? '' : 'OR',
        }));
      });
    };

    useEffect(() => {
      setTotalDocumentsCount(guardViewData?.totalDocumentsCount || 0);
      setTotalFamilyDocumentsCount(guardViewData?.familyDocumentsCount || 0);
      if (
        guardViewData?.queryTextForUI &&
        guardViewData.queryTextForUI.length > 0
      ) {
        isFirstRender.current = false;
        const fetchedQueries = guardViewData.queryTextForUI
          .map((queryArray: string[], index: number) => {
            const criteriaForUI = queryArray[0] || '';
            // Filter out rules that have no actual criteria
            if (!criteriaForUI.trim()) {
              return null; // Mark for removal
            }
            const parsedItems = parseCriteriaString(criteriaForUI);
            const {CriteriaForDB} = buildCriteriaString(parsedItems);

            return {
              SequenceNumber: `${index + 1}`,
              CriteriaForUI: criteriaForUI,
              CriteriaForDB,
              OperatorCode: index === 0 ? '' : 'OR',
            };
          })
          .filter(Boolean) as {
          SequenceNumber: number | string;
          CriteriaForUI: string;
          CriteriaForDB: string;
          OperatorCode: string;
        }[]; // Remove any null entries and assert the correct type

        setQueriesRule(fetchedQueries);
        setRules(fetchedQueries.map((_, i) => `Privileged Rule ${i + 1}`));
      } else {
        // If no valid queries are fetched, ensure both rules and queriesRule are empty
        setQueriesRule([]);
        setRules([]);
      }
    }, [guardViewData, productionId, reset, setValue]);
    const handleAddMatch = () => {
      if (isViewMode) return;
      setComponentIsDirty(true);
      // Validate that there is at least one rule and that none of the rules are empty
      if (!queriesRule || queriesRule.length === 0) {
        getStatus?.(
          true,
          ToastType.Warning,
          t(
            'productionsAndExports.createNewProduction.privGuard.noRuleDataError',
            {
              defaultValue: 'No Rules specified or One of the Rules is empty.',
            }
          )
        );
        return;
      }

      const hasEmptyRule = queriesRule.some(
        (q) =>
          !(q.CriteriaForUI && String(q.CriteriaForUI).trim().length > 0) &&
          !(q.CriteriaForDB && String(q.CriteriaForDB).trim().length > 0)
      );

      if (hasEmptyRule) {
        getStatus?.(
          true,
          ToastType.Warning,
          t(
            'productionsAndExports.createNewProduction.privGuard.noRuleDataError',
            {
              defaultValue: 'No Rules specified or One of the Rules is empty.',
            }
          )
        );
        return;
      }

      getProductionGuardMatchResults(
        {
          ProductionId: productionId,
          IsMarkComplete: false,
          SecurityGroupId: securityGroupId,
          ProductionGuardQueries: queriesRule,
        },
        {
          onSuccess: (savedData) => {
            setTotalDocumentsCount(
              savedData.guardInformation.totalDocumentsCount
            );
            setTotalFamilyDocumentsCount(
              savedData.guardInformation.familyDocumentsCount
            );
          },
          onError: (err) => {
            // Notify parent to show error toast
            getStatus?.(true, ToastType.Error, err?.message);
          },
        }
      );
    };

    // --- State for Modals and Flyouts ---
    const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
    const [showDocSelectionWarning, setShowDocSelectionWarning] =
      useState(false);
    const [showNoRulesWarning, setShowNoRulesWarning] = useState(false);
    const [showTagsFlyout, setShowTagsFlyout] = useState(false);
    // Counter to force remount of TagsTreePanel when cancel is clicked
    const [tagsPanelResetKey, setTagsPanelResetKey] = useState(0);
    // State for the tags selected within the placeholder modal
    const [placeholderPrivTags, setPlaceholderPrivTags] = useState<TagItem[]>(
      []
    );
    // State for the placeholder text, lifted up from the modal
    const [placeholderText, setPlaceholderText] = useState<string | number>('');
    const handlePlaceholderTextChange = (text: string) => {
      if (isViewMode) return;
      setPlaceholderText(text);
    };
    const [isPrivPlaceholderEnabled, setIsPrivPlaceholderEnabled] =
      useState(false);
    const [showInsertFieldFlyout, setShowInsertFieldFlyout] = useState(false);
    const [insertFieldContext, setInsertFieldContext] = useState({
      currentText: '',
      onInsert: (_field: string | number) => {},
    });

    const metadataFieldOptions: ISelectItem[] = useMemo(
      () =>
        guardViewData?.metaDataDetailsOnGuard?.map((item) => ({
          value: item.metaDataValue,
          label: item.metaDataValue,
        })) || [],
      [guardViewData?.metaDataDetailsOnGuard]
    );
    const productionFieldOptions: ISelectItem[] = useMemo(
      () =>
        guardViewData?.productionFieldsOnGuard?.map((item) => ({
          value: item.metaDataValue,
          label: item.metaDataValue,
        })) || [],
      [guardViewData?.productionFieldsOnGuard]
    );
    const tagTreeData = useMemo(() => {
      if (guardViewData?.tagList) {
        const transformed = transformTagTreeData(
          guardViewData?.tagList,
          'PrivilegedTag'
        );
        return transformed;
      }
      return [];
    }, [guardViewData, securityGroupId, productionSetId, productionId]);

    useEffect(() => {
      if (guardViewData) {
        setIsPrivPlaceholderEnabled(
          guardViewData.enablePrivDocPlaceHolder ?? false
        );
      }
    }, [tagTreeData]);

    const handleOpenTagsFromModal = () => {
      if (isViewMode) return;
      // This function will be called from the modal.
      // It closes the modal and opens the tags panel, which lives in this component.
      setShowPlaceholderModal(false);
      setShowTagsFlyout(true);
    };

    const handlePlaceholderTagsChange = (tags: TagItem[]) => {
      if (isViewMode) return;
      setPlaceholderPrivTags(tags);
      // Here you could also update a larger form state if needed
    };

    const handleOpenInsertFieldFromModal = (currentText: string) => {
      if (isViewMode) return;
      setShowPlaceholderModal(false);
      setShowInsertFieldFlyout(true);
      setInsertFieldContext({
        currentText: currentText,
        onInsert: (field) => {
          // Update the placeholder text state and manage UI visibility
          setPlaceholderText((prevText) => prevText + `$${field}$`);
          setShowInsertFieldFlyout(false);
          setShowPlaceholderModal(true);
        },
      });
    };

    const convertTreeViewToTagItem = (
      item: TreeViewBaseItem<ExtendedTreeItemProps>
    ): TagItem => ({
      tagId: Number.parseInt(item.id),
      tagLabel: item.label || '',
    });

    const handleSelectTagsFromPanel = (
      items: TreeViewBaseItem<ExtendedTreeItemProps>[]
    ) => {
      if (isViewMode) return;
      // 1. Convert the selected items to the TagItem format.
      const newTags = items.map(convertTreeViewToTagItem);
      // 2. Update the state for the placeholder tags.
      setPlaceholderPrivTags(newTags);
      // 3. Close the tags flyout panel.
      setShowTagsFlyout(false);
      // 4. Re-open the placeholder modal to show the selected tags.
      setShowPlaceholderModal(true);
    };

    // Convert placeholderPrivTags to TagsTreePanel format by finding actual tree nodes
    // Using findNodesByIds utility function
    const selectedTagsForTreePanel = useMemo(() => {
      if (placeholderPrivTags.length === 0 || tagTreeData.length === 0) {
        return [];
      }
      const selectedIds = placeholderPrivTags.map((tag) => String(tag.tagId));
      return findNodesByIds(tagTreeData, selectedIds);
    }, [placeholderPrivTags, tagTreeData]);

    // --- Navigation/Proceed Logic ---
    const proceedToNextStep = () => {
      setValue('action', FormAction.NEXT);
      handleSubmit(onSubmit)();
    };

    /**
     * Handles the "Next" button click and executes the validation logic
     */
    const handleNextClick = () => {
      const enablePrivDocPlaceholder =
        guardViewData?.enablePrivDocPlaceHolder ?? false;
      const doesContainPrivilegeDocs =
        guardViewData?.doesContainPrivilegeDocs ?? false;

      const haveRulesBeenAdded = queriesRule.length > 0;

      // Scenario 1: "enable for priv doc" is OFF, but priv docs are selected.
      if (!enablePrivDocPlaceholder && doesContainPrivilegeDocs) {
        setShowPlaceholderModal(true);
        return;
      }

      // Scenario 2: "enable for priv doc" is ON, and priv docs are selected.
      if (enablePrivDocPlaceholder && doesContainPrivilegeDocs) {
        setIsPrivPlaceholderEnabled(false);
        setShowDocSelectionWarning(true);
        return;
      }

      // Scenario 3: No priv docs selected, and no rules have been added.
      if (!doesContainPrivilegeDocs && !haveRulesBeenAdded) {
        setIsPrivPlaceholderEnabled(false);
        setShowNoRulesWarning(true);
        return;
      }

      // Scenario 4: No priv docs selected, but rules have been added.
      // Also covers the case where the feature is on, and no priv docs are selected.
      // In these cases, there is NO pop-up, so we proceed directly.
      proceedToNextStep();
    };

    const handleProceedFromPlaceholderModal = () => {
      // Validation check before proceeding
      if (
        isPrivPlaceholderEnabled &&
        (placeholderPrivTags.length === 0 || !placeholderText)
      ) {
        getStatus?.(
          true,
          ToastType.Error,
          t(
            'productionsAndExports.createNewProduction.privGuard.validationOnNext.placeholderForPrivilegDocsModal.emptySelectionWarning'
          )
        );
        return; // Stop execution
      }
      setShowPlaceholderModal(false);
      proceedToNextStep();
    };

    // Expose handleNextClick to the parent component via ref
    useImperativeHandle(ref, () => ({
      save: () => {
        setValue('action', FormAction.SAVE);
        handleSubmit(onSubmit)();
      },
      next: () => {
        if (isInProgressOrCompleted) {
          goNext?.();
          return;
        }
        handleNextClick();
      },
    }));
    return (
      <form
        className="privGuardContainer"
        onSubmit={handleSubmit(onSubmit)}
        data-testid="privguard-box"
      >
        <Box className="privGuardCard">
          {/* Header (sticky top) */}
          {!isTemplate && (
            <Box className="cardHeader">
              <Typography
                className="typo-text subhead2_size "
                data-testid={`priv-guard-header-text`}
              >
                {t(
                  'productionsAndExports.createNewProduction.privGuard.headerText'
                )}
              </Typography>
            </Box>
          )}

          {/* Scrollable Content */}
          <Box className="cardContent">
            <Box className="section">
              <Typography
                variant="subtitle1"
                className="typo-text subhead4_size sectionHeading"
                data-testid={`priv-guard-priv-guard-document-label`}
              >
                {t(
                  'productionsAndExports.createNewProduction.privGuard.privGuardDocumentLabel'
                )}
              </Typography>
              <Typography
                variant="body2"
                className="typo-text body3_size sectionText"
                data-testid={`priv-guard-priv-guard-document-desc`}
              >
                {t(
                  'productionsAndExports.createNewProduction.privGuard.privGuardDocumentDesc'
                )}
              </Typography>
            </Box>

            <Box className="section">
              <Typography
                variant="subtitle1"
                className="typo-text subhead4_size sectionHeading"
                data-testid={`priv-guard-priv-guard-document-rule`}
              >
                {t(
                  'productionsAndExports.createNewProduction.privGuard.privGuardDocumentRule'
                )}
              </Typography>
              <Typography
                variant="body2"
                className="typo-text body3_size sectionText"
                data-testid={`priv-guard-priv-guard-document-rule-desc`}
              >
                {t(
                  'productionsAndExports.createNewProduction.privGuard.privGuardDocumentRuleDesc'
                )}
              </Typography>
            </Box>
            {rules.length > 0 && (
              <Box className="rules-container">
                <div className="privileged-rule" data-testid={`priv-guard-div`}>
                  {rules.map((ruleTitle, index) => (
                    <React.Fragment key={index}>
                      <RuleCard
                        key={`${ruleTitle}-${index}`}
                        title={ruleTitle}
                        onDelete={() => handleDeleteRule(index)}
                        productionId={productionId}
                        handleChange={(data) => handleQueryUpdate(index, data)}
                        initialItems={
                          queriesRule[index]
                            ? parseCriteriaString(
                                queriesRule[index].CriteriaForUI
                              )
                            : []
                        }
                        isProductionSet={isProductionSet}
                        isFirstRender={isFirstRender}
                        disabled={isViewMode || isInProgressOrCompleted}
                        dataTestId={`rule-${index}`}
                      />
                      {rules?.length > index + 1 && (
                        <div data-testid={`priv-guard-or-${index}`}>
                          <ArrButton
                            variant="secondary"
                            size="small"
                            disabled
                            dataTestId={`priv-guard-or-button-${index}`}
                          >
                            OR
                          </ArrButton>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </Box>
            )}
          </Box>

          {/* Footer (sticky bottom) */}
          {!isTemplate && (
            <Box className="cardFooter">
              <ArrButton
                variant="secondary"
                size="small"
                dataTestId="arr-btn-addMatchButton"
                onClick={handleAddMatch}
                disabled={isViewMode || !queriesRule.length}
              >
                {t(
                  'productionsAndExports.createNewProduction.privGuard.privGuardDocumentMatchLabel'
                )}
              </ArrButton>
              <ArrButton
                variant="primary"
                size="small"
                onClick={handleAddRule}
                dataTestId="arr-btn-addRuleButton"
                disabled={isViewMode}
              >
                +{' '}
                {t(
                  'productionsAndExports.createNewProduction.privGuard.privGuardDocumentAddBtn'
                )}
              </ArrButton>
            </Box>
          )}
        </Box>
        <Box className="rightPanel">
          {/* Matched Documents Section */}
          <Box className="matchedDocs">
            <Typography
              variant="h3"
              className="typo-text display3_size matchedCount"
              data-testid={`priv-guard-total-docs-count`}
            >
              {totalDocumentsCount}
            </Typography>
            <Typography
              variant="body2"
              className="typo-text subhead3_size matchedText"
              data-testid={`priv-guard-priv-guard-doc-match`}
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardDocMatch'
              )}
            </Typography>
            <Typography
              variant="body2"
              className="typo-text body3_size matchedDesc"
              data-testid={`priv-guard-priv-guard-doc-match-dec`}
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardDocMatchDec',
                {
                  totalFamilyDocumentsCount: totalFamilyDocumentsCount ?? 0,
                }
              )}
            </Typography>

            <Typography
              variant="body2"
              className="typo-text label2_size viewDocsLabel"
              data-testid={`priv-guard-priv-guard-doc-view-doc`}
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardDocViewDoc'
              )}{' '}
              :
            </Typography>
            <div
              className="viewDocsOptionsContainer"
              data-testid={`priv-guard-priv-guard-doc-list-priv-guard-doc-view`}
            >
              <Button
                variant="text"
                className="typo-text buttonSmall_size viewDocsOption"
                onClick={handleViewInDocList}
                disabled={
                  isViewMode || totalDocumentsCount === 0 || isDocListLoading
                }
                data-testid="priv-guard-button"
              >
                <span
                  className="btn-content"
                  data-testid={`priv-guard-priv-guard-doc-list`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.privGuard.privGuardDocList'
                  )}
                  {isDocListClickLoading && <ArrLoadingSpinner size={16} />}
                </span>
              </Button>
              <Button
                variant="text"
                className="typo-text buttonSmall_size viewDocsOption"
                onClick={handleViewInDocView}
                disabled={
                  isViewMode || totalDocumentsCount === 0 || isDocViewLoading
                }
                data-testid="priv-guard-priv-guard-doc-view-button"
              >
                {t(
                  'productionsAndExports.createNewProduction.privGuard.privGuardDocView'
                )}
                {isDocViewClickLoading && <ArrLoadingSpinner size={16} />}
              </Button>
            </div>

            <Typography
              variant="body2"
              className="typo-text label3_size placeholderText"
              data-testid={`priv-guard-priv-guard-privileged`}
            >
              *
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardPrivileged'
              )}
            </Typography>
          </Box>

          {/* Divider with text */}
          <Box className="dividerWithText">
            <Box className="divider" />
            <Typography
              variant="caption"
              className="typo-text label3_size dividerText"
              data-testid={`priv-guard-priv-guard-optional`}
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardOptional'
              )}
            </Typography>
            <Box className="divider" />
          </Box>

          {/* Categorization Section */}
          <Box className="optionalSection">
            <Typography
              variant="subtitle1"
              className="typo-text subhead3_size sectionHeading"
              data-testid={`priv-guard-priv-guard-category-doc`}
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardCategoryDoc'
              )}
            </Typography>
            <Typography
              variant="body2"
              className="typo-text body3_size sectionText"
              data-testid={`priv-guard-priv-guard-category-desc`}
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardCategoryDesc'
              )}
            </Typography>
            <ArrButton
              variant="text"
              onClick={handleCategorizationClick}
              className="typo-text buttonSmall_size runCategorization"
              disabled={isViewMode}
              dataTestId="priv-guard-priv-guard-run-category-button"
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardRunCategory'
              )}
            </ArrButton>
          </Box>
        </Box>
        {/* Scenario 1 Modal */}
        <PlaceholderForPrivDocModal
          isOpen={showPlaceholderModal}
          onClose={() => !isViewMode && setShowPlaceholderModal(false)}
          onProceed={handleProceedFromPlaceholderModal}
          onOpenTags={handleOpenTagsFromModal}
          selectedTags={placeholderPrivTags}
          onTagsChange={handlePlaceholderTagsChange}
          onOpenInsertField={handleOpenInsertFieldFromModal}
          placeholderText={String(placeholderText)}
          onPlaceholderTextChange={handlePlaceholderTextChange}
          isPlaceholderEnabled={isPrivPlaceholderEnabled}
          onPlaceholderToggle={(enabled) => {
            if (!isViewMode) setIsPrivPlaceholderEnabled(enabled);
          }}
          disabled={isViewMode}
        />
        {/* Scenario 2 Modal */}
        <DocumentSelectionWarning
          isOpen={showDocSelectionWarning}
          onClose={() => setShowDocSelectionWarning(false)}
          onProceed={() => {
            setIsPrivPlaceholderEnabled(false);
            proceedToNextStep();
          }}
        />
        {/* Scenario 3 Modal */}
        <NoRulesAddedWarning
          isOpen={showNoRulesWarning}
          onClose={() => setShowNoRulesWarning(false)}
          onProceed={() => {
            setIsPrivPlaceholderEnabled(false);
            proceedToNextStep();
          }}
        />
        {/* The TagsTreePanel is now rendered here, at the top level */}
        <TagsTreePanel
          key={`tag-panel-${placeholderPrivTags.length}-${placeholderPrivTags.map((t) => t.tagId).join('-')}-${tagsPanelResetKey}`}
          title={t(
            'productionsAndExports.createNewProduction.datComponent.selectTags'
          )}
          type="flyout"
          tags={tagTreeData}
          selectedTags={selectedTagsForTreePanel}
          isOpen={showTagsFlyout}
          onClose={() => {
            if (isViewMode) return;
            setShowTagsFlyout(false);
            // Reset the panel by incrementing the key, forcing a remount with fresh state
            setTagsPanelResetKey((prev) => prev + 1);
            setShowPlaceholderModal(true); // Re-open the modal on close
          }}
          setSelectedTags={handleSelectTagsFromPanel}
        />
        {/* Drawer for Insert Field */}
        <InsertFieldPanel
          isOpen={showInsertFieldFlyout}
          onClose={() => {
            if (isViewMode) return;
            setShowInsertFieldFlyout(false);
            setShowPlaceholderModal(true); // Re-open the modal on close
          }}
          onInsert={(field) => {
            if (isViewMode) return;
            // Call the context function to update text and close/re-open modals
            insertFieldContext.onInsert(field);
          }}
          insertFieldTypes={getInsertFieldTypes()}
          metadataFieldOptions={metadataFieldOptions}
          productionFieldOptions={productionFieldOptions}
        />
      </form>
    );
  }
);

export default PrivGuard;
